import type Context from "@core/Context/Context";
import { createEventEmitter, type Event } from "@core/Event/EventEmitter";
import type CompressOptions from "@core/FileProvider/model/CompressOptions";
import type Hasher from "@core/Hash/Hasher";
import type { Hashable } from "@core/Hash/Hasher";
import assertMaxFileSize from "@core/Resource/assertMaxFileSize";
import type ResourceMovements from "@core/Resource/models/ResourceMovements";
import createNewFilePathUtils from "@core/utils/createNewFilePathUtils";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import { addEvent, Level, traced } from "@ext/loggers/opentelemetry";
import type { Buffer } from "buffer";
import type FileProvider from "../FileProvider/model/FileProvider";
import Path from "../FileProvider/Path/Path";

export type ResourceManagerEvents = Event<
	"content-read",
	{ path: Path; ctx?: Context; content: Buffer; out: { out: Buffer | true | null } }
>;

class ResourceManager implements Hashable {
	private _resources: Path[];
	private _events = createEventEmitter<ResourceManagerEvents>();

	constructor(
		private _fp: FileProvider,
		private _basePath: Path,
		private _rootPath?: Path,
		private _fallbackAbsolutePathResolver?: (path: Path) => Path,
	) {
		this._resources = [];
	}

	get basePath() {
		return this._basePath;
	}

	get rootPath() {
		return this._rootPath;
	}

	get resources() {
		return this._resources;
	}

	get events() {
		return this._events;
	}

	getAllPaths(): Path[] {
		return this._resources;
	}

	setNewBasePath(newBasePath: Path, ignoredResources?: Path[]): ResourceMovements {
		if (newBasePath.compare(this._basePath)) {
			return { oldResources: this._resources, newResources: this._resources };
		}
		const oldResources = [...this._resources];
		const absoluteNewBasePath = this._rootPath ? this._rootPath.join(newBasePath) : newBasePath;
		const newResources = this._resources.map((resource) => {
			const absoluteResource = this.getAbsolutePath(resource);
			if (ignoredResources?.some((ignoredResource) => ignoredResource.compare(absoluteResource))) return resource;

			const relativePath = absoluteNewBasePath.getRelativePath(absoluteResource);
			return relativePath;
		});
		this._basePath = newBasePath;
		this._resources = newResources;
		return { oldResources, newResources };
	}

	async move(oldPath: Path, newPath: Path): Promise<ResourceMovements> {
		if (oldPath.compare(newPath)) {
			return { oldResources: this._resources, newResources: this._resources };
		}
		const oldResources = [...this._resources];
		const newResources = [];
		for (const encodedResource of this._resources) {
			const resource = new Path(decodeURIComponent(encodedResource.value));
			let newResource = new Path(`./${resource.nameWithExtension.replaceAll(oldPath.name, newPath.name)}`);
			const absoluteResource = this.getAbsolutePath(resource);
			let newAbsoluteResource = this.getAbsolutePath(newResource);
			if (!(await this._fp.exists(absoluteResource))) {
				newResources.push(newResource);
				continue;
			}

			if (await this._fp.exists(newAbsoluteResource)) {
				const basePath = newAbsoluteResource.parentDirectoryPath;
				const brothers = (await this._fp.getItems(basePath)).map((b) => b.path);
				const renamePath = createNewFilePathUtils.create(
					newAbsoluteResource,
					brothers,
					newAbsoluteResource.name,
					`.${newAbsoluteResource.extension}`,
				);
				newAbsoluteResource = renamePath;
				newResource = new Path(`./${renamePath.nameWithExtension}`);
			}

			await this._fp.move(absoluteResource, newAbsoluteResource);
			newResources.push(newResource);
		}
		this._resources = newResources;
		return { oldResources, newResources };
	}

	async setContent(path: Path, data: string | Buffer, compress?: CompressOptions) {
		this._assertMaxFileSize(data);
		const out = { data };
		return await this._fp.write(this.getAbsolutePath(path), out.data, compress);
	}

	async getContent(path: Path, ctx?: Context, silent?: boolean): Promise<Buffer> {
		return traced(
			"ResourceManager.getContent",
			{ level: Level.Internal, args: [path?.value, this._basePath?.value], omitResult: true },
			async () => {
				let content = null;
				const absolutePath = await this._getReadableAbsolutePath(path);

				try {
					content = await this._fp.readAsBinary(absolutePath);
				} catch (e) {
					addEvent("read-failed", Level.Commands, {
						abs: absolutePath?.value,
						error: e instanceof Error ? e.message : String(e),
					});
				}

				const out = { out: null };

				if (!silent)
					await this._events.emit("content-read", { path: this._basePath.join(path), ctx, content, out });

				if (out.out === true) return await this.getContent(path, ctx, true);
				return out.out ?? content;
			},
		);
	}

	async hash(hash: Hasher) {
		await this._resources.forEachAsync(async (resource) => {
			hash.hash(await this.getContent(resource));
		});
		return hash;
	}

	async delete(path: Path) {
		const absolutePath = await this._getReadableAbsolutePath(path);
		if (!(await this._fp.exists(absolutePath))) return;
		return await this._fp.delete(absolutePath, true);
	}

	async deleteAll() {
		await Promise.all(this._resources.map(async (path) => await this.delete(path)));
	}

	set(path: Path) {
		if (this._resources.findIndex((p) => p.compare(path)) === -1) {
			this._resources.push(path);
		}
	}

	async exists(path: Path) {
		return await this._fp.exists(await this._getReadableAbsolutePath(path));
	}

	getAbsolutePath(path: Path): Path {
		return this._rootPath
			? this._rootPath.parentDirectoryPath.join(this._getJoinRootPath().join(path))
			: this._basePath.join(path);
	}

	async assertExists(src: Path, error: { title: string; message: string }) {
		if (!(await this.exists(src))) throw new DefaultError(error.message, null, null, false, error.title);
	}

	private _assertMaxFileSize(data: string | Buffer) {
		assertMaxFileSize(data.length ?? (data as unknown as ArrayBuffer).byteLength);
	}

	private async _getReadableAbsolutePath(path: Path): Promise<Path> {
		const absolutePath = this.getAbsolutePath(path);
		if (await this._fp.exists(absolutePath)) return absolutePath;

		const fallbackAbsolutePath = this._fallbackAbsolutePathResolver?.(path);
		if (fallbackAbsolutePath && (await this._fp.exists(fallbackAbsolutePath))) {
			return fallbackAbsolutePath;
		}

		return absolutePath;
	}

	private _getJoinRootPath(basePath?: Path): Path {
		return this._rootPath
			? this._rootPath.parentDirectoryPath.subDirectory(this._rootPath).join(basePath ?? this._basePath)
			: this._basePath;
	}
}

export default ResourceManager;
